import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  'en-US': {
    translation: {
      // Landing Page
      landing: {
        hero: {
          headline: 'Transform your Jiu-Jitsu Academy',
          subheadline: 'Complete management system for modern academies. Control students, finances, classes, and progression in one platform.',
          ctaStart: 'Start Free Trial',
          ctaDemo: 'Schedule Demo',
          noCreditCard: 'No credit card • Setup in 5 minutes • Support in English'
        },
        demoModal: {
          title: 'Access Free Demo',
          subtitle: 'Receive your credentials by email and test for 48 hours',
          nameLabel: 'Full Name',
          namePlaceholder: 'Your name',
          emailLabel: 'Email',
          emailPlaceholder: 'your@email.com',
          academyLabel: 'Academy Name',
          academyPlaceholder: 'Your academy name',
          submitButton: 'Get Demo Access',
          submitting: 'Sending...',
          successTitle: 'Email Sent! 🎉',
          successMessage: 'Check your inbox to access the demo.',
          validityMessage: 'Valid for 48 hours',
          consentText: 'By requesting the demo, you agree to receive emails from MyDojo. Your credentials expire in 48 hours.',
          errors: {
            allFieldsRequired: 'Please fill in all fields',
            invalidEmail: 'Please enter a valid email',
            sendFailed: 'Error sending credentials. Please try again.'
          }
        },
        features: {
          title: 'Everything your Academy Needs',
          subtitle: 'Complete professional features for managing your academy',
          studentManagement: {
            title: 'Student Management',
            description: 'Complete system for managing students, belts, and progression'
          },
          scheduling: {
            title: 'Class Scheduling',
            description: 'Smart calendar with automated check-in'
          },
          financial: {
            title: 'Financial Control',
            description: 'Financial dashboard with real-time metrics'
          },
          beltProgression: {
            title: 'Belt Progression',
            description: 'Professional system for belt evaluation and promotion'
          },
          communication: {
            title: 'Integrated Communication',
            description: 'Automatic notifications for students and parents'
          },
          analytics: {
            title: 'Reports & Analytics',
            description: 'Detailed insights about academy performance'
          },
          digitalWaivers: {
            title: 'Digital Terms',
            description: 'Digital waivers system with electronic signature'
          },
          studentPortal: {
            title: 'Student Portal',
            description: 'Dedicated app for students to track their progress'
          }
        },
        pricing: {
          title: 'Simple and Transparent Pricing',
          subtitle: 'No hidden fees. No surprises. Cancel anytime.',
          plan: 'PROFESSIONAL PLAN',
          perMonth: '/month',
          fullAccess: 'Full access to all features',
          benefits: {
            qrCheckIn: 'Automated QR check-in',
            stripeIntegration: 'Stripe integration for payments',
            beltProgression: 'Belt progression system',
            financialReports: 'Detailed financial reports',
            massCommunication: 'Mass communication via email/SMS',
            studentPortal: 'Custom student portal',
            support: 'Dedicated technical support'
          },
          ctaStart: 'Start Now',
          ctaSales: 'Talk to Sales'
        },
        cta: {
          title: 'Ready to modernize your Academy?',
          subtitle: 'Join hundreds of academies that transformed their management with MyDojo',
          ctaTrial: 'Free Trial for 14 Days',
          ctaDemo: 'View Demo'
        },
        footer: {
          description: 'Complete management system for Jiu-Jitsu academies.',
          rights: 'All rights reserved.',
          contact: 'contact@mydojo.info'
        }
      },
      // Navigation & Common
      navigation: {
        dashboard: 'Dashboard',
        students: 'Students',
        attendance: 'Attendance',
        finances: 'Finances',
        studentPortal: 'Student Portal',
        beltProgression: 'Belt Progression',
        scheduleManagement: 'Schedule Management',
        communicationCenter: 'Communication Center',
        expenseManagement: 'Expense Management',
        menu: 'Menu',
        logout: 'Logout',
        login: 'Login',
        checkIn: 'Check In',
        profile: 'Profile',
        progress: 'Progress',
        schedule: 'Schedule'
      },
      // Common actions & buttons
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        search: 'Search',
        filter: 'Filter',
        export: 'Export',
        import: 'Import',
        submit: 'Submit',
        close: 'Close',
        view: 'View',
        loading: 'Loading',
        settings: 'Settings',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        clear: 'Clear',
        selectAll: 'Select All',
        actions: 'Actions',
        addNew: 'Add New',
        overview: 'Overview'
      },
      // Expense Management
      expenses: {
        title: 'Expense Management',
        description: 'Comprehensive financial tracking and automated expense calculations for academy operations.',
        monthlyAmount: 'Monthly Amount',
        yearlyAmount: 'Yearly Amount',
        dueDate: 'Due Date',
        status: 'Status',
        variance: 'Variance',
        trend: 'Trend',
        // Categories
        categories: {
          rent: 'Rent & Lease',
          salaries: 'Employee Salaries',
          utilities: 'Utilities',
          cleaning: 'Cleaning Supplies',
          insurance: 'Insurance'
        },
        // Summary sections
        monthlySummary: 'Monthly Summary',
        totalExpenses: 'Total Expenses',
        budget: 'Budget',
        vsBudget: 'vs Budget',
        quickStats: 'Quick Stats',
        categoriesOverBudget: 'Categories Over Budget',
        largestExpense: 'Largest Expense',
        category: 'Category',
        projectedMonthEnd: 'Projected Month End',
        recentExpenses: 'Recent Expenses',
        // Tabs
        addExpense: 'Add Expense',
        salaryCalculator: 'Salary Calculator',
        reports: 'Reports',
        budgetAnalysis: 'Budget Analysis',
        // Expense details
        subcategory: 'Subcategory',
        amount: 'Amount',
        vendor: 'Vendor',
        date: 'Date',
        receipt: 'Receipt',
        // Status values
        current: 'Current',
        calculated: 'Calculated',
        lowStock: 'Low Stock',
        approved: 'Approved',
        paid: 'Paid',
        stable: 'Stable',
        up: 'Up',
        down: 'Down'
      },
      // Academy Inventory Management
      inventory: {
        title: 'Academy Inventory',
        description: 'Complete inventory management for kimonos, shirts, and cleaning supplies with automated reorder tracking.',
        // Categories
        categories: {
          kimonos: 'Kimonos',
          shirts: 'Shirts & Apparel',
          cleaning: 'Cleaning Supplies'
        },
        // Kimono items
        kimonos: {
          adult: {
            white: 'Adult Gi - White',
            blue: 'Adult Gi - Blue',
            black: 'Adult Gi - Black'
          },
          kids: {
            white: 'Kids Gi - White',
            blue: 'Kids Gi - Blue'
          }
        },
        // Shirt items
        shirts: {
          academy: {
            polo: 'Academy Polo Shirt',
            tshirt: 'Academy T-Shirt'
          },
          rashguard: {
            longSleeve: 'Rashguard Long Sleeve',
            shortSleeve: 'Rashguard Short Sleeve'
          },
          compression: {
            shorts: 'Compression Shorts'
          }
        },
        // Cleaning items
        cleaning: {
          matCleaner: 'Mat Cleaner',
          disinfectant: 'Multi-Surface Disinfectant',
          handSanitizer: 'Hand Sanitizer',
          paperTowels: 'Paper Towels',
          floorCleaner: 'Floor Cleaner',
          antibacterialWipes: 'Antibacterial Wipes'
        },
        // Units
        units: {
          pieces: 'pieces',
          bottles: 'bottles',
          rolls: 'rolls',
          packs: 'packs'
        },
        // Status
        status: {
          critical: 'Critical',
          lowStock: 'Low Stock',
          inStock: 'In Stock',
          wellStocked: 'Well Stocked'
        },
        // Statistics
        stats: {
          items: 'Items',
          lowStock: 'Low Stock',
          value: 'Value',
          lowStockItems: 'Low Stock Items',
          totalItems: 'Total Items',
          inventoryValue: 'Inventory Value',
          autoReorder: 'Auto-Reorder'
        },
        // Table headers
        table: {
          item: 'Item',
          stockLevel: 'Stock Level',
          status: 'Status',
          costUnit: 'Cost/Unit',
          lastPurchased: 'Last Purchased',
          reorderIn: 'Reorder In',
          actions: 'Actions',
          size: 'Size'
        },
        // Search and filters
        search: {
          placeholder: 'Search items, category, size, or vendor...'
        },
        filters: {
          allCategories: 'All Categories',
          allStatuses: 'All Statuses'
        },
        // Actions
        actions: {
          addItem: 'Add Item',
          addNew: 'Add new inventory item',
          updateStock: 'Update stock for',
          reorder: 'Reorder',
          increase: 'Increase stock',
          decrease: 'Decrease stock'
        },
        // Reorder
        reorder: {
          now: 'Reorder Now',
          days: 'days'
        },
        // Auto-reorder settings
        autoReorder: {
          title: 'Auto-Reorder Settings',
          triggers: 'Trigger Conditions',
          vendors: 'Preferred Vendors',
          settings: 'Order Settings',
          minThreshold: 'Stock falls below minimum threshold',
          monthlyUsage: 'Monthly usage rate calculation',
          leadTime: 'Lead time consideration (3-5 business days)',
          maxLevel: 'Order up to maximum stock level',
          bulkDiscount: 'Bulk discount optimization',
          notifications: 'Email notifications enabled'
        },
        // Vendors
        vendors: {
          primary: 'Koral Kimonos (Primary Gis)',
          secondary: 'Custom Apparel Co (Shirts)',
          bulk: 'CleanCorp Supplies (Cleaning)'
        }
      },
      // Currency & Financial
      currency: {
        monthlyRevenue: 'Monthly Recurring Revenue (MRR)',
        churnRate: 'Churn Rate',
        averageTicket: 'Average Ticket',
        delinquency: 'Delinquency',
        revenueVsExpenses: 'Revenue vs Expenses',
        expenseBreakdown: 'Expense Breakdown',
        recentTransactions: 'Recent Transactions'
      },
      financial: {
        payment: 'Payment',
        expense: 'Expense',
        billing: 'SaaS Billing',
        completed: 'Completed',
        pending: 'Pending',
        failed: 'Failed',
        cancelled: 'Cancelled',
        revenue: 'Revenue',
        expenses: 'Expenses',
        profit: 'Profit',
        balance: 'Balance'
      },
      // User roles
      roles: {
        owner: 'Academy Owner',
        coach: 'Coach',
        student: 'Student',
        admin: 'Administrator'
      },
      // Belt progression
      belts: {
        white: 'White',
        blue: 'Blue',
        purple: 'Purple',
        brown: 'Brown',
        black: 'Black',
        grey: 'Grey',
        yellow: 'Yellow',
        orange: 'Orange',
        green: 'Green',
        red: 'Red'
      },
      // Academy features
      academy: {
        students: 'Students',
        classes: 'Classes',
        instructors: 'Instructors',
        payments: 'Payments',
        reports: 'Reports',
        announcements: 'Announcements',
        calendar: 'Calendar',
        attendance: 'Attendance',
        progression: 'Belt Progression',
        communication: 'Communication'
      },
      // Months
      months: {
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December'
      },
      // Academy Registration
      registration: {
        header: {
          title: 'Join the platform trusted by martial arts academies worldwide'
        },
        steps: {
          academyInfo: 'Academy Info',
          academyInfoDesc: 'Basic academy details',
          ownerDetails: 'Owner Details',
          ownerDetailsDesc: 'Account owner information',
          billingSetup: 'Billing Setup',
          billingSetupDesc: 'Pricing and payment setup',
          complete: 'Complete',
          completeDesc: 'Finalize registration'
        },
        step1: {
          title: 'Academy Information',
          subtitle: 'Tell us about your martial arts academy',
          academyName: 'Academy Name',
          academyNamePlaceholder: 'Elite BJJ Academy',
          subdomain: 'Your Academy URL',
          subdomainPlaceholder: 'elite-bjj',
          subdomainHelper: 'This will be your academy\'s unique web address',
          address: 'Address',
          addressPlaceholder: '123 Main Street',
          city: 'City',
          cityPlaceholder: 'Los Angeles',
          state: 'State/Province',
          statePlaceholder: 'CA',
          zipCode: 'ZIP/Postal Code',
          zipCodePlaceholder: '90001',
          country: 'Country',
          phone: 'Phone',
          phonePlaceholder: '+1 (555) 123-4567',
          website: 'Website',
          websitePlaceholder: 'https://yoursite.com'
        },
        step2: {
          title: 'Owner Information',
          subtitle: 'Create your account as academy owner',
          firstName: 'First Name',
          firstNamePlaceholder: 'John',
          lastName: 'Last Name',
          lastNamePlaceholder: 'Doe',
          email: 'Email Address',
          emailPlaceholder: 'john@academy.com',
          ownerPhone: 'Phone Number',
          ownerPhonePlaceholder: '+1 (555) 123-4567',
          password: 'Password',
          passwordPlaceholder: 'Minimum 6 characters',
          confirmPassword: 'Confirm Password',
          confirmPasswordPlaceholder: 'Re-enter password'
        },
        step3: {
          title: 'Billing Setup',
          subtitle: 'Configure your academy pricing',
          estimatedStudents: 'Estimated Number of Students',
          averageMonthlyFee: 'Average Monthly Fee per Student',
          pricingPreview: 'Your Pricing Preview',
          baseRate: 'Base Platform Rate',
          studentFees: 'Student Management Fees',
          totalMonthly: 'Total Monthly Cost',
          perMonth: '/month',
          pricingNote: 'Transparent pricing based on your academy size',
          tier1: '1% fee for first 50 students',
          tier2: '2% fee for students 51-100',
          tier3: '5% fee for students 101+',
          trialNote: '60-day free trial included'
        },
        step4: {
          title: 'Terms & Conditions',
          subtitle: 'Review and accept our terms',
          acceptTerms: 'I accept the Terms of Service',
          acceptPrivacy: 'I accept the Privacy Policy',
          finalNote: 'By completing registration, you agree to our terms and start your 60-day free trial',
          completeButton: 'Complete Registration',
          completing: 'Creating your academy...'
        },
        buttons: {
          next: 'Next Step',
          back: 'Back',
          register: 'Complete Registration'
        },
        errors: {
          academyNameRequired: 'Academy name is required',
          subdomainRequired: 'Subdomain is required',
          cityRequired: 'City is required',
          stateRequired: 'State is required',
          firstNameRequired: 'Owner first name is required',
          lastNameRequired: 'Owner last name is required',
          emailRequired: 'Owner email is required',
          emailInvalid: 'Valid email is required',
          passwordRequired: 'Password must be at least 6 characters',
          passwordMismatch: 'Passwords do not match',
          studentsRequired: 'Please estimate your student count',
          feeRequired: 'Please enter average monthly fee',
          termsRequired: 'Please accept the terms of service',
          privacyRequired: 'Please accept the privacy policy',
          subdomainTaken: 'Subdomain is already taken. Please choose another.',
          registrationFailed: 'Registration failed. Please try again.'
        }
      }
    }
  },
  'pt-BR': {
    translation: {
      // Landing Page
      landing: {
        hero: {
          headline: 'Transforme sua Academia de Jiu-Jitsu',
          subheadline: 'Sistema completo de gestão para academias modernas. Controle alunos, finanças, aulas e progressão em uma única plataforma.',
          ctaStart: 'Começar Teste Grátis',
          ctaDemo: 'Agendar Demo',
          noCreditCard: 'Sem cartão de crédito • Configuração em 5 minutos • Suporte em português'
        },
        demoModal: {
          title: 'Acesse a Demo Gratuita',
          subtitle: 'Receba suas credenciais por email e teste por 48 horas',
          nameLabel: 'Nome Completo',
          namePlaceholder: 'Seu nome',
          emailLabel: 'Email',
          emailPlaceholder: 'seu@email.com',
          academyLabel: 'Nome da Academia',
          academyPlaceholder: 'Nome da sua academia',
          submitButton: 'Receber Acesso à Demo',
          submitting: 'Enviando...',
          successTitle: 'Email Enviado! 🎉',
          successMessage: 'Verifique sua caixa de entrada para acessar a demo.',
          validityMessage: 'Válido por 48 horas',
          consentText: 'Ao solicitar a demo, você concorda em receber emails do MyDojo. Suas credenciais expiram em 48 horas.',
          errors: {
            allFieldsRequired: 'Por favor, preencha todos os campos',
            invalidEmail: 'Por favor, insira um email válido',
            sendFailed: 'Erro ao enviar credenciais. Tente novamente.'
          }
        },
        features: {
          title: 'Tudo que sua Academia Precisa',
          subtitle: 'Funcionalidades completas para gestão profissional da sua academia',
          studentManagement: {
            title: 'Gestão de Alunos',
            description: 'Sistema completo para gerenciar alunos, faixas e progressão'
          },
          scheduling: {
            title: 'Agendamento de Aulas',
            description: 'Calendário inteligente com check-in automatizado'
          },
          financial: {
            title: 'Controle Financeiro',
            description: 'Dashboard financeiro com métricas em tempo real'
          },
          beltProgression: {
            title: 'Progressão de Faixas',
            description: 'Sistema profissional para avaliação e promoção de faixas'
          },
          communication: {
            title: 'Comunicação Integrada',
            description: 'Notificações automáticas para alunos e responsáveis'
          },
          analytics: {
            title: 'Relatórios e Analytics',
            description: 'Insights detalhados sobre performance da academia'
          },
          digitalWaivers: {
            title: 'Termos Digitais',
            description: 'Sistema de waivers digitais com assinatura eletrônica'
          },
          studentPortal: {
            title: 'Portal do Aluno',
            description: 'App dedicado para alunos acompanharem seu progresso'
          }
        },
        pricing: {
          title: 'Preço Simples e Transparente',
          subtitle: 'Sem taxas ocultas. Sem surpresas. Cancele quando quiser.',
          plan: 'PLANO PROFISSIONAL',
          perMonth: '/mês',
          fullAccess: 'Acesso completo a todas as funcionalidades',
          benefits: {
            qrCheckIn: 'Check-in automatizado com código QR',
            stripeIntegration: 'Integração com Stripe para pagamentos',
            beltProgression: 'Sistema de progressão de faixas',
            financialReports: 'Relatórios financeiros detalhados',
            massCommunication: 'Comunicação em massa via email/SMS',
            studentPortal: 'Portal personalizado para alunos',
            support: 'Suporte técnico dedicado'
          },
          ctaStart: 'Começar Agora',
          ctaSales: 'Falar com Vendas'
        },
        cta: {
          title: 'Pronto para Modernizar sua Academia?',
          subtitle: 'Junte-se a centenas de academias que já transformaram sua gestão com MyDojo',
          ctaTrial: 'Teste Grátis por 14 Dias',
          ctaDemo: 'Ver Demonstração'
        },
        footer: {
          description: 'Sistema completo de gestão para academias de Jiu-Jitsu.',
          rights: 'Todos os direitos reservados.',
          contact: 'contato@mydojo.info'
        }
      },
      // Navegação e Comum
      navigation: {
        dashboard: 'Painel',
        students: 'Alunos',
        attendance: 'Presença',
        finances: 'Finanças',
        studentPortal: 'Portal do Aluno',
        beltProgression: 'Progressão de Faixas',
        scheduleManagement: 'Gerenciamento de Horários',
        communicationCenter: 'Centro de Comunicação',
        expenseManagement: 'Gestão de Despesas',
        menu: 'Menu',
        logout: 'Sair',
        login: 'Entrar',
        checkIn: 'Check-in',
        profile: 'Perfil',
        progress: 'Progresso',
        schedule: 'Horário'
      },
      // Ações e botões comuns
      common: {
        save: 'Salvar',
        cancel: 'Cancelar',
        delete: 'Excluir',
        edit: 'Editar',
        add: 'Adicionar',
        search: 'Pesquisar',
        filter: 'Filtrar',
        export: 'Exportar',
        import: 'Importar',
        submit: 'Enviar',
        close: 'Fechar',
        view: 'Visualizar',
        loading: 'Carregando',
        settings: 'Configurações',
        back: 'Voltar',
        next: 'Próximo',
        previous: 'Anterior',
        clear: 'Limpar',
        selectAll: 'Selecionar Tudo',
        actions: 'Ações',
        addNew: 'Adicionar Novo',
        overview: 'Visão Geral'
      },
      // Gestão de Despesas
      expenses: {
        title: 'Gestão de Despesas',
        description: 'Controle financeiro abrangente e cálculos automatizados de despesas para operações da academia.',
        monthlyAmount: 'Valor Mensal',
        yearlyAmount: 'Valor Anual',
        dueDate: 'Data de Vencimento',
        status: 'Status',
        variance: 'Variação',
        trend: 'Tendência',
        // Categorias
        categories: {
          rent: 'Aluguel e Locação',
          salaries: 'Salários de Funcionários',
          utilities: 'Utilidades',
          cleaning: 'Produtos de Limpeza',
          insurance: 'Seguro'
        },
        // Seções de resumo
        monthlySummary: 'Resumo Mensal',
        totalExpenses: 'Total de Despesas',
        budget: 'Orçamento',
        vsBudget: 'vs Orçamento',
        quickStats: 'Estatísticas Rápidas',
        categoriesOverBudget: 'Categorias Acima do Orçamento',
        largestExpense: 'Maior Despesa',
        category: 'Categoria',
        projectedMonthEnd: 'Projeção Fim do Mês',
        recentExpenses: 'Despesas Recentes',
        // Abas
        addExpense: 'Adicionar Despesa',
        salaryCalculator: 'Calculadora de Salário',
        reports: 'Relatórios',
        budgetAnalysis: 'Análise Orçamentária',
        // Detalhes de despesas
        subcategory: 'Subcategoria',
        amount: 'Valor',
        vendor: 'Fornecedor',
        date: 'Data',
        receipt: 'Recibo',
        // Valores de status
        current: 'Atual',
        calculated: 'Calculado',
        lowStock: 'Estoque Baixo',
        approved: 'Aprovado',
        paid: 'Pago',
        stable: 'Estável',
        up: 'Subindo',
        down: 'Descendo'
      },
      // Gestão de Inventário da Academia
      inventory: {
        title: 'Inventário da Academia',
        description: 'Gestão completa de inventário para kimonos, camisas e produtos de limpeza com rastreamento automatizado de reposição.',
        // Categorias
        categories: {
          kimonos: 'Kimonos',
          shirts: 'Camisas e Vestimentas',
          cleaning: 'Produtos de Limpeza'
        },
        // Itens de kimono
        kimonos: {
          adult: {
            white: 'Kimono Adulto - Branco',
            blue: 'Kimono Adulto - Azul',
            black: 'Kimono Adulto - Preto'
          },
          kids: {
            white: 'Kimono Infantil - Branco',
            blue: 'Kimono Infantil - Azul'
          }
        },
        // Itens de camisa
        shirts: {
          academy: {
            polo: 'Polo da Academia',
            tshirt: 'Camiseta da Academia'
          },
          rashguard: {
            longSleeve: 'Rashguard Manga Longa',
            shortSleeve: 'Rashguard Manga Curta'
          },
          compression: {
            shorts: 'Shorts de Compressão'
          }
        },
        // Itens de limpeza
        cleaning: {
          matCleaner: 'Limpador de Tatame',
          disinfectant: 'Desinfetante Multi-Superfície',
          handSanitizer: 'Álcool Gel',
          paperTowels: 'Papel Toalha',
          floorCleaner: 'Limpador de Piso',
          antibacterialWipes: 'Lenços Antibacterianos'
        },
        // Unidades
        units: {
          pieces: 'peças',
          bottles: 'frascos',
          rolls: 'rolos',
          packs: 'pacotes'
        },
        // Status
        status: {
          critical: 'Crítico',
          lowStock: 'Estoque Baixo',
          inStock: 'Em Estoque',
          wellStocked: 'Bem Estocado'
        },
        // Estatísticas
        stats: {
          items: 'Itens',
          lowStock: 'Estoque Baixo',
          value: 'Valor',
          lowStockItems: 'Itens com Estoque Baixo',
          totalItems: 'Total de Itens',
          inventoryValue: 'Valor do Inventário',
          autoReorder: 'Reposição Automática'
        },
        // Cabeçalhos da tabela
        table: {
          item: 'Item',
          stockLevel: 'Nível de Estoque',
          status: 'Status',
          costUnit: 'Custo/Unidade',
          lastPurchased: 'Última Compra',
          reorderIn: 'Repor em',
          actions: 'Ações',
          size: 'Tamanho'
        },
        // Busca e filtros
        search: {
          placeholder: 'Buscar itens, categoria, tamanho ou fornecedor...'
        },
        filters: {
          allCategories: 'Todas as Categorias',
          allStatuses: 'Todos os Status'
        },
        // Ações
        actions: {
          addItem: 'Adicionar Item',
          addNew: 'Adicionar novo item ao inventário',
          updateStock: 'Atualizar estoque para',
          reorder: 'Repor',
          increase: 'Aumentar estoque',
          decrease: 'Diminuir estoque'
        },
        // Reposição
        reorder: {
          now: 'Repor Agora',
          days: 'dias'
        },
        // Configurações de reposição automática
        autoReorder: {
          title: 'Configurações de Reposição Automática',
          triggers: 'Condições de Acionamento',
          vendors: 'Fornecedores Preferenciais',
          settings: 'Configurações do Pedido',
          minThreshold: 'Estoque fica abaixo do limite mínimo',
          monthlyUsage: 'Cálculo da taxa de uso mensal',
          leadTime: 'Consideração do tempo de entrega (3-5 dias úteis)',
          maxLevel: 'Pedir até o nível máximo de estoque',
          bulkDiscount: 'Otimização de desconto por volume',
          notifications: 'Notificações por email habilitadas'
        },
        // Fornecedores
        vendors: {
          primary: 'Koral Kimonos (Kimonos Principais)',
          secondary: 'Custom Apparel Co (Camisas)',
          bulk: 'CleanCorp Supplies (Limpeza)'
        }
      },
      // Moeda e Financeiro
      currency: {
        monthlyRevenue: 'Receita Mensal Recorrente (MRR)',
        churnRate: 'Taxa de Churn',
        averageTicket: 'Ticket Médio',
        delinquency: 'Inadimplência',
        revenueVsExpenses: 'Receitas vs Despesas',
        expenseBreakdown: 'Breakdown de Despesas',
        recentTransactions: 'Transações Recentes'
      },
      financial: {
        payment: 'Pagamento',
        expense: 'Despesa',
        billing: 'Cobrança SaaS',
        completed: 'Concluído',
        pending: 'Pendente',
        failed: 'Falhou',
        cancelled: 'Cancelado',
        revenue: 'Receita',
        expenses: 'Despesas',
        profit: 'Lucro',
        balance: 'Saldo'
      },
      // Funções de usuário
      roles: {
        owner: 'Proprietário da Academia',
        coach: 'Professor',
        student: 'Aluno',
        admin: 'Administrador'
      },
      // Progressão de faixas
      belts: {
        white: 'Branca',
        blue: 'Azul',
        purple: 'Roxa',
        brown: 'Marrom',
        black: 'Preta',
        grey: 'Cinza',
        yellow: 'Amarela',
        orange: 'Laranja',
        green: 'Verde',
        red: 'Vermelha'
      },
      // Funcionalidades da academia
      academy: {
        students: 'Alunos',
        classes: 'Aulas',
        instructors: 'Instrutores',
        payments: 'Pagamentos',
        reports: 'Relatórios',
        announcements: 'Anúncios',
        calendar: 'Calendário',
        attendance: 'Presença',
        progression: 'Progressão de Faixas',
        communication: 'Comunicação'
      },
      // Meses
      months: {
        january: 'Janeiro',
        february: 'Fevereiro',
        march: 'Março',
        april: 'Abril',
        may: 'Maio',
        june: 'Junho',
        july: 'Julho',
        august: 'Agosto',
        september: 'Setembro',
        october: 'Outubro',
        november: 'Novembro',
        december: 'Dezembro'
      },
      // Registro da Academia
      registration: {
        header: {
          title: 'Junte-se à plataforma confiada por academias de artes marciais em todo o mundo'
        },
        steps: {
          academyInfo: 'Info da Academia',
          academyInfoDesc: 'Detalhes básicos da academia',
          ownerDetails: 'Dados do Proprietário',
          ownerDetailsDesc: 'Informações do proprietário',
          billingSetup: 'Configuração de Cobrança',
          billingSetupDesc: 'Configuração de preços e pagamento',
          complete: 'Concluir',
          completeDesc: 'Finalizar registro'
        },
        step1: {
          title: 'Informações da Academia',
          subtitle: 'Conte-nos sobre sua academia de artes marciais',
          academyName: 'Nome da Academia',
          academyNamePlaceholder: 'Academia Elite de BJJ',
          subdomain: 'URL da sua Academia',
          subdomainPlaceholder: 'academia-elite',
          subdomainHelper: 'Este será o endereço web único da sua academia',
          address: 'Endereço',
          addressPlaceholder: 'Rua Principal, 123',
          city: 'Cidade',
          cityPlaceholder: 'São Paulo',
          state: 'Estado',
          statePlaceholder: 'SP',
          zipCode: 'CEP',
          zipCodePlaceholder: '01234-567',
          country: 'País',
          phone: 'Telefone',
          phonePlaceholder: '+55 (11) 98765-4321',
          website: 'Website',
          websitePlaceholder: 'https://seusite.com.br'
        },
        step2: {
          title: 'Informações do Proprietário',
          subtitle: 'Crie sua conta como proprietário da academia',
          firstName: 'Primeiro Nome',
          firstNamePlaceholder: 'João',
          lastName: 'Sobrenome',
          lastNamePlaceholder: 'Silva',
          email: 'Endereço de Email',
          emailPlaceholder: 'joao@academia.com.br',
          ownerPhone: 'Número de Telefone',
          ownerPhonePlaceholder: '+55 (11) 98765-4321',
          password: 'Senha',
          passwordPlaceholder: 'Mínimo 6 caracteres',
          confirmPassword: 'Confirmar Senha',
          confirmPasswordPlaceholder: 'Digite a senha novamente'
        },
        step3: {
          title: 'Configuração de Cobrança',
          subtitle: 'Configure os preços da sua academia',
          estimatedStudents: 'Número Estimado de Alunos',
          averageMonthlyFee: 'Mensalidade Média por Aluno',
          pricingPreview: 'Prévia do seu Preço',
          baseRate: 'Taxa Base da Plataforma',
          studentFees: 'Taxas de Gestão de Alunos',
          totalMonthly: 'Custo Mensal Total',
          perMonth: '/mês',
          pricingNote: 'Preços transparentes baseados no tamanho da sua academia',
          tier1: 'Taxa de 1% para os primeiros 50 alunos',
          tier2: 'Taxa de 2% para alunos 51-100',
          tier3: 'Taxa de 5% para alunos 101+',
          trialNote: 'Teste grátis de 60 dias incluído'
        },
        step4: {
          title: 'Termos e Condições',
          subtitle: 'Revise e aceite nossos termos',
          acceptTerms: 'Aceito os Termos de Serviço',
          acceptPrivacy: 'Aceito a Política de Privacidade',
          finalNote: 'Ao concluir o registro, você concorda com nossos termos e inicia seu teste grátis de 60 dias',
          completeButton: 'Concluir Registro',
          completing: 'Criando sua academia...'
        },
        buttons: {
          next: 'Próximo Passo',
          back: 'Voltar',
          register: 'Concluir Registro'
        },
        errors: {
          academyNameRequired: 'Nome da academia é obrigatório',
          subdomainRequired: 'Subdomínio é obrigatório',
          cityRequired: 'Cidade é obrigatória',
          stateRequired: 'Estado é obrigatório',
          firstNameRequired: 'Primeiro nome do proprietário é obrigatório',
          lastNameRequired: 'Sobrenome do proprietário é obrigatório',
          emailRequired: 'Email do proprietário é obrigatório',
          emailInvalid: 'Email válido é obrigatório',
          passwordRequired: 'Senha deve ter pelo menos 6 caracteres',
          passwordMismatch: 'As senhas não coincidem',
          studentsRequired: 'Por favor, estime o número de alunos',
          feeRequired: 'Por favor, insira a mensalidade média',
          termsRequired: 'Por favor, aceite os termos de serviço',
          privacyRequired: 'Por favor, aceite a política de privacidade',
          subdomainTaken: 'Subdomínio já está em uso. Por favor, escolha outro.',
          registrationFailed: 'Registro falhou. Por favor, tente novamente.'
        }
      }
    }
  },
  'es-ES': {
    translation: {
      // Landing Page
      landing: {
        hero: {
          headline: 'Transforme su academia de Jiu-Jitsu',
          subheadline: 'Sistema completo de gestión para academias modernas. Controlar estudiantes, finanzas, clases y progresión en una única plataforma.',
          ctaStart: 'Comenzar prueba gratuita',
          ctaDemo: 'Programar demo',
          noCreditCard: 'Sin tarjeta de crédito • Configuración en 5 minutos • Soporte en español'
        },
        features: {
          title: 'Todo lo que su academia necesita',
          subtitle: 'Funcionalidades completas para la gestión profesional de su academia',
          studentManagement: {
            title: 'Gestión de estudiantes',
            description: 'Sistema completo para gestionar estudiantes, cinturones y progresión'
          },
          scheduling: {
            title: 'Programación de clases',
            description: 'Calendario inteligente con check-in automatizado'
          },
          financial: {
            title: 'Control financiero',
            description: 'Dashboard financiero con métricas en tiempo real'
          },
          beltProgression: {
            title: 'Progresión de cinturones',
            description: 'Sistema profesional para evaluación y promoción de cinturones'
          },
          communication: {
            title: 'Comunicación integrada',
            description: 'Notificaciones automáticas para estudiantes y padres'
          },
          analytics: {
            title: 'Informes y análisis',
            description: 'Insights detallados sobre el rendimiento de la academia'
          },
          digitalWaivers: {
            title: 'Términos digitales',
            description: 'Sistema de waivers digitales con firma electrónica'
          },
          studentPortal: {
            title: 'Portal del estudiante',
            description: 'App dedicada para estudiantes a seguir su progreso'
          }
        },
        pricing: {
          title: 'Precios simples y transparentes',
          subtitle: 'Sin tarifas ocultas. Sin sorpresas. Cancele cuando quiera.',
          plan: 'PLAN PROFESIONAL',
          perMonth: '/mes',
          fullAccess: 'Acceso completo a todas las funcionalidades',
          benefits: {
            qrCheckIn: 'Check-in automatizado con código QR',
            stripeIntegration: 'Integración con Stripe para pagos',
            beltProgression: 'Sistema de progresión de cinturones',
            financialReports: 'Informes financieros detallados',
            massCommunication: 'Comunicación masiva por correo/SMS',
            studentPortal: 'Portal personalizado para estudiantes',
            support: 'Soporte técnico dedicado'
          },
          ctaStart: 'Comenzar ahora',
          ctaSales: 'Hablar con ventas'
        },
        cta: {
          title: 'Listo para modernizar su academia?',
          subtitle: 'Únase a cientos de academias que ya transformaron su gestión con MyDojo',
          ctaTrial: 'Prueba gratuita durante 14 días',
          ctaDemo: 'Ver demostración'
        },
        footer: {
          description: 'Sistema completo de gestión para academias de Jiu-Jitsu.',
          rights: 'Todos los derechos reservados.',
          contact: 'contacto@mydojo.info'
        }
      },
      // Navegación y Común
      navigation: {
        dashboard: 'Panel',
        students: 'Estudiantes',
        attendance: 'Asistencia',
        finances: 'Finanzas',
        studentPortal: 'Portal del Estudiante',
        beltProgression: 'Progresión de Cinturones',
        scheduleManagement: 'Gestión de Horarios',
        communicationCenter: 'Centro de Comunicación',
        expenseManagement: 'Gestión de Gastos',
        menu: 'Menú',
        logout: 'Cerrar Sesión',
        login: 'Iniciar Sesión',
        checkIn: 'Registrarse',
        profile: 'Perfil',
        progress: 'Progreso',
        schedule: 'Horario'
      },
      // Acciones y botones comunes
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        add: 'Agregar',
        search: 'Buscar',
        filter: 'Filtrar',
        export: 'Exportar',
        import: 'Importar',
        submit: 'Enviar',
        close: 'Cerrar',
        view: 'Ver',
        loading: 'Cargando',
        settings: 'Configuraciones',
        back: 'Atrás',
        next: 'Siguiente',
        previous: 'Anterior',
        clear: 'Limpiar',
        selectAll: 'Seleccionar Todo',
        actions: 'Acciones',
        addNew: 'Agregar Nuevo',
        overview: 'Resumen'
      },
      // Gestión de Gastos
      expenses: {
        title: 'Gestión de Gastos',
        description: 'Seguimiento financiero integral y cálculos automatizados de gastos para operaciones de la academia.',
        monthlyAmount: 'Monto Mensual',
        yearlyAmount: 'Monto Anual',
        dueDate: 'Fecha de Vencimiento',
        status: 'Estado',
        variance: 'Variación',
        trend: 'Tendencia',
        // Categorías
        categories: {
          rent: 'Alquiler y Arrendamiento',
          salaries: 'Salarios de Empleados',
          utilities: 'Servicios Públicos',
          cleaning: 'Productos de Limpieza',
          insurance: 'Seguro'
        },
        // Secciones de resumen
        monthlySummary: 'Resumen Mensual',
        totalExpenses: 'Gastos Totales',
        budget: 'Presupuesto',
        vsBudget: 'vs Presupuesto',
        quickStats: 'Estadísticas Rápidas',
        categoriesOverBudget: 'Categorías Sobre Presupuesto',
        largestExpense: 'Gasto Mayor',
        category: 'Categoría',
        projectedMonthEnd: 'Proyección Fin de Mes',
        recentExpenses: 'Gastos Recientes',
        // Pestañas
        addExpense: 'Agregar Gasto',
        salaryCalculator: 'Calculadora de Salario',
        reports: 'Informes',
        budgetAnalysis: 'Análisis de Presupuesto',
        // Detalles de gastos
        subcategory: 'Subcategoría',
        amount: 'Monto',
        vendor: 'Proveedor',
        date: 'Fecha',
        receipt: 'Recibo',
        // Valores de estado
        current: 'Actual',
        calculated: 'Calculado',
        lowStock: 'Stock Bajo',
        approved: 'Aprobado',
        paid: 'Pagado',
        stable: 'Estable',
        up: 'Subiendo',
        down: 'Bajando'
      },
      // Gestión de Inventario de la Academia
      inventory: {
        title: 'Inventario de la Academia',
        description: 'Gestión completa de inventario para kimonos, camisas y productos de limpieza con seguimiento automatizado de reposición.',
        // Categorías
        categories: {
          kimonos: 'Kimonos',
          shirts: 'Camisas y Vestimenta',
          cleaning: 'Productos de Limpieza'
        },
        // Artículos de kimono
        kimonos: {
          adult: {
            white: 'Kimono Adulto - Blanco',
            blue: 'Kimono Adulto - Azul',
            black: 'Kimono Adulto - Negro'
          },
          kids: {
            white: 'Kimono Infantil - Blanco',
            blue: 'Kimono Infantil - Azul'
          }
        },
        // Artículos de camisa
        shirts: {
          academy: {
            polo: 'Polo de la Academia',
            tshirt: 'Camiseta de la Academia'
          },
          rashguard: {
            longSleeve: 'Rashguard Manga Larga',
            shortSleeve: 'Rashguard Manga Corta'
          },
          compression: {
            shorts: 'Shorts de Compresión'
          }
        },
        // Artículos de limpieza
        cleaning: {
          matCleaner: 'Limpiador de Colchoneta',
          disinfectant: 'Desinfectante Multi-Superficie',
          handSanitizer: 'Desinfectante de Manos',
          paperTowels: 'Toallas de Papel',
          floorCleaner: 'Limpiador de Piso',
          antibacterialWipes: 'Toallitas Antibacterianas'
        },
        // Unidades
        units: {
          pieces: 'piezas',
          bottles: 'botellas',
          rolls: 'rollos',
          packs: 'paquetes'
        },
        // Estado
        status: {
          critical: 'Crítico',
          lowStock: 'Stock Bajo',
          inStock: 'En Stock',
          wellStocked: 'Bien Abastecido'
        },
        // Estadísticas
        stats: {
          items: 'Artículos',
          lowStock: 'Stock Bajo',
          value: 'Valor',
          lowStockItems: 'Artículos con Stock Bajo',
          totalItems: 'Total de Artículos',
          inventoryValue: 'Valor del Inventario',
          autoReorder: 'Reposición Automática'
        },
        // Encabezados de tabla
        table: {
          item: 'Artículo',
          stockLevel: 'Nivel de Stock',
          status: 'Estado',
          costUnit: 'Costo/Unidad',
          lastPurchased: 'Última Compra',
          reorderIn: 'Reponer en',
          actions: 'Acciones',
          size: 'Tamaño'
        },
        // Búsqueda y filtros
        search: {
          placeholder: 'Buscar artículos, categoría, tamaño o proveedor...'
        },
        filters: {
          allCategories: 'Todas las Categorías',
          allStatuses: 'Todos los Estados'
        },
        // Acciones
        actions: {
          addItem: 'Agregar Artículo',
          addNew: 'Agregar nuevo artículo al inventario',
          updateStock: 'Actualizar stock para',
          reorder: 'Reponer',
          increase: 'Aumentar stock',
          decrease: 'Disminuir stock'
        },
        // Reposición
        reorder: {
          now: 'Reponer Ahora',
          days: 'días'
        },
        // Configuraciones de reposición automática
        autoReorder: {
          title: 'Configuraciones de Reposición Automática',
          triggers: 'Condiciones de Activación',
          vendors: 'Proveedores Preferidos',
          settings: 'Configuraciones de Pedido',
          minThreshold: 'Stock cae por debajo del umbral mínimo',
          monthlyUsage: 'Cálculo de tasa de uso mensal',
          leadTime: 'Consideración de tiempo de entrega (3-5 días hábiles)',
          maxLevel: 'Pedir hasta el nivel máximo de stock',
          bulkDiscount: 'Optimización de descuento por volumen',
          notifications: 'Notificaciones por email habilitadas'
        },
        // Proveedores
        vendors: {
          primary: 'Koral Kimonos (Kimonos Principales)',
          secondary: 'Custom Apparel Co (Camisas)',
          bulk: 'CleanCorp Supplies (Limpieza)'
        }
      },
      // Moneda y Financiero
      currency: {
        monthlyRevenue: 'Ingresos Recurrentes Mensuales (MRR)',
        churnRate: 'Tasa de Abandono',
        averageTicket: 'Ticket Promedio',
        delinquency: 'Morosidad',
        revenueVsExpenses: 'Ingresos vs Gastos',
        expenseBreakdown: 'Desglose de Gastos',
        recentTransactions: 'Transacciones Recientes'
      },
      financial: {
        payment: 'Pago',
        expense: 'Gasto',
        billing: 'Facturación SaaS',
        completed: 'Completado',
        pending: 'Pendiente',
        failed: 'Fallido',
        cancelled: 'Cancelado',
        revenue: 'Ingresos',
        expenses: 'Gastos',
        profit: 'Ganancia',
        balance: 'Balance'
      },
      // Roles de usuario
      roles: {
        owner: 'Propietario de la Academia',
        coach: 'Entrenador',
        student: 'Estudiante',
        admin: 'Administrador'
      },
      // Progresión de cinturones
      belts: {
        white: 'Blanco',
        blue: 'Azul',
        purple: 'Morado',
        brown: 'Marrón',
        black: 'Negro',
        grey: 'Gris',
        yellow: 'Amarillo',
        orange: 'Naranja',
        green: 'Verde',
        red: 'Rojo'
      },
      // Funcionalidades de la academia
      academy: {
        students: 'Estudiantes',
        classes: 'Clases',
        instructors: 'Instructores',
        payments: 'Pagos',
        reports: 'Informes',
        announcements: 'Anuncios',
        calendar: 'Calendario',
        attendance: 'Asistencia',
        progression: 'Progresión de Cinturones',
        communication: 'Comunicación'
      },
      // Meses
      months: {
        january: 'Enero',
        february: 'Febrero',
        march: 'Marzo',
        april: 'Abril',
        may: 'Mayo',
        june: 'Junio',
        july: 'Julio',
        august: 'Agosto',
        september: 'Septiembre',
        october: 'Octubre',
        november: 'Noviembre',
        december: 'Diciembre'
      },
      // Registro de la Academia
      registration: {
        header: {
          title: 'Únase a la plataforma confiada por academias de artes marciales en todo el mundo'
        },
        steps: {
          academyInfo: 'Info de la Academia',
          academyInfoDesc: 'Detalles básicos de la academia',
          ownerDetails: 'Datos del Propietario',
          ownerDetailsDesc: 'Información del propietario',
          billingSetup: 'Configuración de Facturación',
          billingSetupDesc: 'Configuración de precios y pago',
          complete: 'Completar',
          completeDesc: 'Finalizar registro'
        },
        step1: {
          title: 'Información de la Academia',
          subtitle: 'Cuéntenos sobre su academia de artes marciales',
          academyName: 'Nombre de la Academia',
          academyNamePlaceholder: 'Academia Elite de BJJ',
          subdomain: 'URL de su Academia',
          subdomainPlaceholder: 'academia-elite',
          subdomainHelper: 'Esta será la dirección web única de su academia',
          address: 'Dirección',
          addressPlaceholder: 'Calle Principal, 123',
          city: 'Ciudad',
          cityPlaceholder: 'Madrid',
          state: 'Estado/Provincia',
          statePlaceholder: 'Madrid',
          zipCode: 'Código Postal',
          zipCodePlaceholder: '28001',
          country: 'País',
          phone: 'Teléfono',
          phonePlaceholder: '+34 912 345 678',
          website: 'Sitio Web',
          websitePlaceholder: 'https://suacademia.com'
        },
        step2: {
          title: 'Información del Propietario',
          subtitle: 'Cree su cuenta como propietario de la academia',
          firstName: 'Nombre',
          firstNamePlaceholder: 'Juan',
          lastName: 'Apellido',
          lastNamePlaceholder: 'García',
          email: 'Dirección de Email',
          emailPlaceholder: 'juan@academia.com',
          ownerPhone: 'Número de Teléfono',
          ownerPhonePlaceholder: '+34 912 345 678',
          password: 'Contraseña',
          passwordPlaceholder: 'Mínimo 6 caracteres',
          confirmPassword: 'Confirmar Contraseña',
          confirmPasswordPlaceholder: 'Vuelva a ingresar la contraseña'
        },
        step3: {
          title: 'Configuración de Facturación',
          subtitle: 'Configure los precios de su academia',
          estimatedStudents: 'Número Estimado de Estudiantes',
          averageMonthlyFee: 'Cuota Mensual Promedio por Estudiante',
          pricingPreview: 'Vista Previa de su Precio',
          baseRate: 'Tarifa Base de la Plataforma',
          studentFees: 'Tarifas de Gestión de Estudiantes',
          totalMonthly: 'Costo Mensal Total',
          perMonth: '/mes',
          pricingNote: 'Precios transparentes basados en el tamaño de su academia',
          tier1: 'Tarifa del 1% para los primeros 50 estudiantes',
          tier2: 'Tarifa del 2% para estudiantes 51-100',
          tier3: 'Tarifa del 5% para estudiantes 101+',
          trialNote: 'Prueba gratuita de 60 días incluida'
        },
        step4: {
          title: 'Términos y Condiciones',
          subtitle: 'Revise y acepte nuestros términos',
          acceptTerms: 'Acepto los Términos de Servicio',
          acceptPrivacy: 'Acepto la Política de Privacidad',
          finalNote: 'Al completar el registro, acepta nuestros términos e inicia su prueba gratuita de 60 días',
          completeButton: 'Completar Registro',
          completing: 'Creando su academia...'
        },
        buttons: {
          next: 'Siguiente Paso',
          back: 'Atrás',
          register: 'Completar Registro'
        },
        errors: {
          academyNameRequired: 'El nombre de la academia es obligatorio',
          subdomainRequired: 'El subdominio es obligatorio',
          cityRequired: 'La ciudad es obligatoria',
          stateRequired: 'El estado es obligatorio',
          firstNameRequired: 'El nombre del propietario es obligatorio',
          lastNameRequired: 'El apellido del propietario es obligatorio',
          emailRequired: 'El email del propietario es obligatorio',
          emailInvalid: 'Se requiere un email válido',
          passwordRequired: 'La contraseña debe tener al menos 6 caracteres',
          passwordMismatch: 'Las contraseñas no coinciden',
          studentsRequired: 'Por favor, estime el número de estudiantes',
          feeRequired: 'Por favor, ingrese la cuota mensual promedio',
          termsRequired: 'Por favor, acepte los términos de servicio',
          privacyRequired: 'Por favor, acepte la política de privacidad',
          subdomainTaken: 'El subdominio ya está en uso. Por favor, elija otro.',
          registrationFailed: 'El registro falló. Por favor, intente nuevamente.'
        }
      }
    }
  }
};

const detectionOptions = {
  order: ['localStorage', 'navigator', 'cookie', 'htmlTag'],
  lookupLocalStorage: 'i18nextLng',
  lookupCookie: 'i18next',
  caches: ['localStorage', 'cookie'],
  excludeCacheFor: ['cimode']
};

i18n
  ?.use(LanguageDetector)
  ?.use(initReactI18next)
  ?.init({
    resources,
    fallbackLng: 'pt-BR',
    debug: process.env.NODE_ENV === 'development',

    detection: detectionOptions,

    interpolation: {
      escapeValue: false
    },

    react: {
      useSuspense: false
    }
  });

// Initialize language based on geolocation
import { detectUserCountry } from '../services/geolocationService';

detectUserCountry()?.then((geoData) => {
  if (geoData?.language && i18n?.language !== geoData?.language) {
    i18n?.changeLanguage(geoData?.language);
  }
})?.catch((error) => {
  console.warn('Failed to detect user location for language:', error);
});

export default i18n;